import React, { useEffect, useState } from "react";
import { ArrowRight, Check, X } from "lucide-react";
import { recordAnswer } from "@/lib/skillMap";
import { recordSkillEvent } from "@/lib/skillLedger";
import { shuffleQuestionOptions } from "@/lib/seededShuffle";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Sample questions on the Academy Learn tab — the SAME bank the Practice/Master tabs use (no new content).
 *  Tap → immediate right/wrong + authored explanation/misconception note; ledger kind "sample" (never mastery). */
export const SampleQuestions = ({ outcomeCodes, tier = "explorer", skillSlug, onPractice, onMaster, count = 3,
  title, practiceLabel = "Practice (7 questions)", masterLabel = "Master test (10)", ledger = { surface: "academy" }, className = "" }) => {
  const [qs, setQs] = useState(undefined);
  const [picks, setPicks] = useState({});
  const [notes, setNotes] = useState({});
  const childId = (() => { try { return localStorage.getItem("we_child_id_v1") || "anon"; } catch { return "anon"; } })();
  useEffect(() => {
    let off = false;
    fetch(`${API}/questions/select`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ child_id: childId, outcome_codes: outcomeCodes, age_tier: tier, count, language: "en" }) })
      .then((r) => (r.ok ? r.json() : { questions: [] }))
      .then((j) => { if (!off) setQs((j.questions || []).slice(0, count).map((q, i) => shuffleQuestionOptions(q, `sample:${skillSlug}:${q.id || i}`))); })
      .catch(() => { if (!off) setQs([]); });
    return () => { off = true; };
  }, [skillSlug, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  if (qs === undefined) return <p className="text-sm font-bold text-slate-500" data-testid="sample-questions-loading">Loading sample questions…</p>;
  if (!qs.length) return null;

  const pick = (q, opt) => {
    if (picks[q.id] !== undefined) return;
    const ok = String(opt).trim() === String(q.correct_answer).trim();
    setPicks((p) => ({ ...p, [q.id]: opt }));
    if (ledger.skillId) recordSkillEvent({ skillId: ledger.skillId, correct: ok, kind: "sample", skillType: ledger.skillType || null });
    else recordAnswer({ surface: ledger.surface || "academy", raw: ledger.raw || skillSlug, correct: ok, kind: "sample", skillType: ledger.skillType || null });
    fetch(`${API}/questions/answer`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ child_id: childId, question_id: q.id, was_correct: ok, chosen: String(opt) }) })
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (j?.feedback?.note) setNotes((n) => ({ ...n, [q.id]: j.feedback.note })); }).catch(() => {});
  };
  const answered = Object.keys(picks).length;
  const right = qs.filter((q) => picks[q.id] !== undefined && String(picks[q.id]).trim() === String(q.correct_answer).trim()).length;

  return (
    <section className={`rounded-2xl border-[3px] border-slate-900 bg-sky-50 p-4 md:p-5 shadow-[0_4px_0_0_rgba(15,23,42,1)] ${className}`} data-testid="sample-questions" aria-labelledby="sample-questions-title">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">Have a go</p>
      <h3 id="sample-questions-title" className="mt-1 font-heading text-base md:text-xl font-bold">{title || `Try ${qs.length} sample questions`}</h3>
      <ol className="mt-3 space-y-3">
        {qs.map((q, qi) => {
          const chosen = picks[q.id];
          const done = chosen !== undefined;
          const ok = done && String(chosen).trim() === String(q.correct_answer).trim();
          return (
            <li key={q.id || qi} className="rounded-2xl border-[3px] border-slate-900 bg-white p-3" data-testid={`sample-q-${qi}`}>
              <p className="font-bold text-sm md:text-base text-slate-900">{qi + 1}. {q.question_text}</p>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {(q.options || []).map((opt, oi) => {
                  const isRight = String(opt).trim() === String(q.correct_answer).trim();
                  const cls = !done ? "border-slate-900 bg-white hover:bg-yellow-50" : isRight ? "border-emerald-800 bg-emerald-100" : String(chosen) === String(opt) ? "border-rose-700 bg-rose-50" : "border-slate-300 bg-white text-slate-500";
                  return (
                    <li key={oi}>
                      <button type="button" onClick={() => pick(q, opt)} disabled={done} data-testid={`sample-q-${qi}-opt-${oi}`}
                        className={`w-full min-h-[44px] rounded-xl border-2 px-3 py-2 text-left text-sm font-bold flex items-center justify-between gap-2 ${cls}`}>
                        <span>{opt}</span>
                        {done && isRight && <Check size={16} strokeWidth={3} className="text-emerald-800" />}
                        {done && !isRight && String(chosen) === String(opt) && <X size={16} strokeWidth={3} className="text-rose-700" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {done && (
                <div className="mt-2 rounded-xl border-2 border-slate-900 bg-yellow-50 p-2.5" data-testid={`sample-q-${qi}-feedback`}>
                  <p className="text-xs font-extrabold">{ok ? "✓ Yes!" : "Not quite — here's why."}</p>
                  {!ok && notes[q.id] && <p className="mt-0.5 text-xs font-bold text-amber-900">💡 {notes[q.id]}</p>}
                  {q.explanation && <p className="mt-0.5 text-xs font-bold text-slate-700">{q.explanation}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {answered === qs.length && <p className="mr-1 text-sm font-bold text-slate-800" data-testid="sample-questions-score">{right} of {qs.length} — {right === qs.length ? "ready for a full round?" : "the lesson above shows how."}</p>}
        <button type="button" onClick={onPractice} data-testid="sample-go-practice"
          className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]">
          {practiceLabel} <ArrowRight size={14} strokeWidth={3} />
        </button>
        {onMaster && (
          <button type="button" onClick={onMaster} data-testid="sample-go-master"
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]">
            {masterLabel}
          </button>
        )}
      </div>
    </section>
  );
};
